import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * ScrollScene3D — decorative parallax scene of ABSTRACT geometric glyphs
 * (Islamic-inspired rosettes, calligraphic rings, spice orbs, steam wisps).
 * No cartoonish food icons. Fixed to viewport, pointer-events disabled,
 * sits BEHIND the app content via mixBlendMode.
 *
 * Design goals:
 *  - Premium, minimalist, editorial (not childish clip-art)
 *  - Islamic geometric hint (8-pointed star, 12-fold rosette, arabesque arch)
 *  - GPU-accelerated transforms with reduced-motion opt-out
 */

/* ---------- Elegant SVG glyphs (all use currentColor + strokeWidth) ---------- */

const StarRosette = ({ size, stroke = 1.1 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinejoin="round">
    <path d="M50 4 L61 25 L84 22 L79 45 L96 60 L75 68 L74 92 L50 82 L26 92 L25 68 L4 60 L21 45 L16 22 L39 25 Z" />
    <circle cx="50" cy="55" r="14" />
  </svg>
);

const Ring12 = ({ size, stroke = 1.1 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round">
    <circle cx="50" cy="50" r="36" />
    <circle cx="50" cy="50" r="24" />
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i * Math.PI * 2) / 12;
      const x1 = 50 + Math.cos(a) * 24;
      const y1 = 50 + Math.sin(a) * 24;
      const x2 = 50 + Math.cos(a) * 36;
      const y2 = 50 + Math.sin(a) * 36;
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
    })}
  </svg>
);

const Arabesque = ({ size, stroke = 1.1 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round">
    <path d="M50 8 C 30 8, 14 24, 14 50 C 14 76, 30 92, 50 92 C 70 92, 86 76, 86 50 C 86 24, 70 8, 50 8 Z" />
    <path d="M28 50 C 28 38, 38 28, 50 28 C 62 28, 72 38, 72 50" />
    <path d="M72 50 C 72 62, 62 72, 50 72 C 38 72, 28 62, 28 50" />
    <circle cx="50" cy="50" r="4" fill="currentColor" />
  </svg>
);

const Steam = ({ size, stroke = 1.2 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round">
    <path d="M32 88 C 24 74, 44 66, 36 52 C 28 38, 48 30, 40 16" />
    <path d="M52 88 C 44 74, 64 66, 56 52 C 48 38, 68 30, 60 16" />
    <path d="M72 88 C 64 74, 84 66, 76 52 C 68 38, 88 30, 80 16" />
  </svg>
);

const Chalice = ({ size, stroke = 1.15 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinejoin="round" strokeLinecap="round">
    <path d="M22 22 H 78 C 78 44, 66 58, 50 58 C 34 58, 22 44, 22 22 Z" />
    <line x1="50" y1="58" x2="50" y2="80" />
    <line x1="34" y1="82" x2="66" y2="82" />
    <line x1="30" y1="14" x2="70" y2="14" />
  </svg>
);

const CrescentArch = ({ size, stroke = 1.15 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round">
    <path d="M18 90 L 18 50 C 18 30, 34 14, 50 14 C 66 14, 82 30, 82 50 L 82 90" />
    <path d="M18 90 L 82 90" />
    <path d="M34 90 L 34 60" />
    <path d="M66 90 L 66 60" />
  </svg>
);

const Diamond = ({ size, stroke = 1.15 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinejoin="round">
    <path d="M50 8 L 92 50 L 50 92 L 8 50 Z" />
    <path d="M50 24 L 76 50 L 50 76 L 24 50 Z" />
    <line x1="8" y1="50" x2="92" y2="50" />
    <line x1="50" y1="8" x2="50" y2="92" />
  </svg>
);

const Orbit = ({ size, stroke = 1.1 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={stroke}>
    <ellipse cx="50" cy="50" rx="42" ry="16" />
    <ellipse cx="50" cy="50" rx="42" ry="16" transform="rotate(60 50 50)" />
    <ellipse cx="50" cy="50" rx="42" ry="16" transform="rotate(-60 50 50)" />
    <circle cx="50" cy="50" r="6" fill="currentColor" />
  </svg>
);

const Sunburst = ({ size, stroke = 1.05 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round">
    <circle cx="50" cy="50" r="16" />
    {Array.from({ length: 16 }).map((_, i) => {
      const a = (i * Math.PI * 2) / 16;
      const x1 = 50 + Math.cos(a) * 22;
      const y1 = 50 + Math.sin(a) * 22;
      const x2 = 50 + Math.cos(a) * (i % 2 ? 38 : 44);
      const y2 = 50 + Math.sin(a) * (i % 2 ? 38 : 44);
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
    })}
  </svg>
);

const Bowl = ({ size, stroke = 1.15 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round">
    <path d="M12 50 C 12 76, 32 88, 50 88 C 68 88, 88 76, 88 50 Z" />
    <path d="M12 50 L 88 50" />
    <path d="M38 30 C 38 22, 46 18, 50 22 C 54 26, 62 22, 62 30" opacity="0.7" />
  </svg>
);

/* ---------- Scene definition ---------- */
const GLYPHS = {
  star: StarRosette,
  ring: Ring12,
  arabesque: Arabesque,
  steam: Steam,
  chalice: Chalice,
  arch: CrescentArch,
  diamond: Diamond,
  orbit: Orbit,
  sunburst: Sunburst,
  bowl: Bowl,
};

// Layout, depth, motion + brand palette. `themes` still lets admins filter.
const ITEMS = [
  { glyph: "star",      top: "8%",  left: "6%",  z: -220, size: 88,  hue: "#D4AF37", speed: 0.26, spin: 0.30, delay: 0.0, themes: ["umrah", "ramadan"] },
  { glyph: "steam",     top: "18%", left: "82%", z:  180, size: 92,  hue: "#E85A2A", speed: 0.36, spin: 0.22, delay: 0.6, themes: ["beverages", "winter", "ramadan", "umrah"] },
  { glyph: "arabesque", top: "36%", left: "88%", z: -140, size: 96,  hue: "#D4AF37", speed: 0.42, spin: 0.36, delay: 1.1, themes: ["umrah", "ramadan"] },
  { glyph: "ring",      top: "62%", left: "4%",  z:  120, size: 112, hue: "#1B2A5C", speed: 0.32, spin: 0.20, delay: 0.3, themes: ["umrah", "ramadan"] },
  { glyph: "chalice",   top: "72%", left: "78%", z:  -80, size: 84,  hue: "#E85A2A", speed: 0.48, spin: 0.28, delay: 1.4, themes: ["beverages", "winter"] },
  { glyph: "diamond",   top: "50%", left: "50%", z: -320, size: 70,  hue: "#D4AF37", speed: 0.20, spin: 0.40, delay: 0.9, themes: ["umrah", "summer"] },
  { glyph: "orbit",     top: "12%", left: "48%", z:  240, size: 80,  hue: "#1B2A5C", speed: 0.44, spin: 0.26, delay: 0.2, themes: ["umrah", "summer", "ramadan"] },
  { glyph: "sunburst",  top: "88%", left: "18%", z:  200, size: 84,  hue: "#D4AF37", speed: 0.34, spin: 0.34, delay: 1.7, themes: ["summer", "beverages"] },
  { glyph: "arch",      top: "24%", left: "24%", z:  -40, size: 96,  hue: "#1B2A5C", speed: 0.30, spin: 0.20, delay: 0.5, themes: ["umrah", "ramadan"] },
  { glyph: "bowl",      top: "82%", left: "48%", z:  100, size: 86,  hue: "#D4AF37", speed: 0.40, spin: 0.24, delay: 1.0, themes: ["savory", "winter", "ramadan"] },
  { glyph: "star",      top: "40%", left: "14%", z:  260, size: 62,  hue: "#E85A2A", speed: 0.38, spin: 0.32, delay: 0.8, themes: ["desserts", "umrah"] },
  { glyph: "arabesque", top: "56%", left: "92%", z: -180, size: 78,  hue: "#D4AF37", speed: 0.26, spin: 0.30, delay: 1.3, themes: ["umrah", "ramadan"] },
  { glyph: "ring",      top: "6%",  left: "30%", z: -260, size: 66,  hue: "#E85A2A", speed: 0.22, spin: 0.44, delay: 1.6, themes: ["umrah", "summer"] },
  { glyph: "steam",     top: "68%", left: "34%", z:   60, size: 72,  hue: "#1B2A5C", speed: 0.46, spin: 0.20, delay: 0.4, themes: ["beverages", "winter"] },
  { glyph: "sunburst",  top: "30%", left: "68%", z: -100, size: 74,  hue: "#D4AF37", speed: 0.35, spin: 0.30, delay: 1.2, themes: ["summer"] },
  { glyph: "diamond",   top: "78%", left: "62%", z: -240, size: 68,  hue: "#E85A2A", speed: 0.24, spin: 0.28, delay: 0.7, themes: ["savory", "umrah"] },
  { glyph: "chalice",   top: "46%", left: "80%", z:   40, size: 74,  hue: "#1B2A5C", speed: 0.42, spin: 0.26, delay: 1.8, themes: ["beverages"] },
  { glyph: "bowl",      top: "92%", left: "82%", z:  -60, size: 66,  hue: "#D4AF37", speed: 0.33, spin: 0.30, delay: 0.1, themes: ["savory", "summer"] },
  { glyph: "arch",      top: "16%", left: "66%", z:  320, size: 82,  hue: "#D4AF37", speed: 0.50, spin: 0.30, delay: 1.9, themes: ["umrah", "ramadan"] },
];

const THEMES = ["all", "desserts", "savory", "beverages", "ramadan", "summer", "winter", "umrah"];

export default function ScrollScene3D() {
  const [scrollY, setScrollY] = useState(0);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [reduced, setReduced] = useState(false);
  const [density, setDensity] = useState("medium");
  const [theme, setTheme] = useState("all");
  const rafRef = useRef(0);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => {
        const map = Object.fromEntries((r.data || []).map((s) => [s.key, s.value]));
        const d = (map.scene_3d_density || "medium").toLowerCase();
        if (["heavy", "medium", "off"].includes(d)) setDensity(d);
        const th = (map.scene_3d_theme || "all").toLowerCase();
        if (THEMES.includes(th)) setTheme(th);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(!!mq.matches);
    const onChange = () => setReduced(!!mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    if (reduced) return;
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        setScrollY(window.scrollY);
      });
    };
    const onMove = (e) => {
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      setMouse({
        x: (e.clientX / w) * 2 - 1,
        y: (e.clientY / h) * 2 - 1,
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("mousemove", onMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [reduced]);

  if (density === "off") return null;
  const themed = theme === "all" ? ITEMS : ITEMS.filter((it) => it.themes.includes(theme));
  const items = density === "heavy" ? themed : themed.filter((_, i) => i % 2 === 0);
  if (!items.length) return null;

  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 z-30 overflow-hidden pointer-events-none"
      style={{
        perspective: "1200px",
        perspectiveOrigin: "50% 50%",
        mixBlendMode: "multiply",
      }}
      data-testid="scroll-scene-3d"
    >
      <div
        className="absolute inset-0"
        style={{ transformStyle: "preserve-3d" }}
      >
        {items.map((it, i) => {
          const Glyph = GLYPHS[it.glyph];
          if (!Glyph) return null;
          const sy = reduced ? 0 : scrollY;
          const mx = reduced ? 0 : mouse.x;
          const my = reduced ? 0 : mouse.y;

          const translateY = -sy * it.speed;
          const rotateX = sy * it.spin * 0.06 + my * 6;
          const rotateY = sy * it.spin * 0.05 + mx * 12;
          const rotateZ = (sy * it.spin * 0.02) % 360;

          const depthAlpha = Math.max(0.06, 0.22 - Math.abs(it.z) / 1600);

          return (
            <div
              key={i}
              className="absolute floaty"
              style={{
                top: it.top,
                left: it.left,
                width: it.size,
                height: it.size,
                color: it.hue,
                opacity: depthAlpha,
                animationDelay: `${it.delay}s`,
                transform: `translate3d(0, ${translateY}px, ${it.z}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) rotateZ(${rotateZ}deg)`,
                transformStyle: "preserve-3d",
                willChange: "transform, opacity",
                filter: `drop-shadow(0 10px 24px ${it.hue}22)`,
                transition: reduced ? "none" : "transform 120ms linear",
              }}
              data-testid={`scene3d-item-${i}`}
            >
              <Glyph size={it.size} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
